import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, Text, DropdownButton, Alert, Tabs, SearchBar } from '@artprov/design-system';
import { useQuery } from "@tanstack/react-query";
import { getMatchingBancario } from './data/matchingBancario.data';
import { getEstadisticas, parseEstadisticas } from './data/estadisticas.data';
import { AccionesCell } from './components/AccionesCell';
import { PorcentajeMatchCell } from './components/PorcentajeMatchCell';
import { MatchingBancarioTable } from './components/MatchingBancarioTable';
import { formatCurrency } from '../../common/utils/functions/formatCurrency';
import { useMatchingBancarioFiltersStore } from './zustand/filters.store';
import { errorService } from '../../common/utils/functions/error.service';
import { useDebounce } from '../../hooks/useDebounce';
import { useMatchingBancarioUI } from './hooks/useMatchingBancarioUI';
import { useMatchingBancarioActions } from './hooks/useMatchingBancarioActions';
import { IndicadoresGrid } from '../../common/components/IndicadoresGrid';
import { indicadoresBancarioConfig } from './config/indicadores.config';
import { getFechaInicialDesde } from '../../common/utils/functions/dateUtils';
import ModalFilter from './components/ModalFilter';
import ModalConfirm from './components/ModalConfirm';
import ModalResultado from './components/ModalResultado';
import ModalAccionMasiva from '../../common/components/ModalAccionMasiva';
import TagsFilters from './components/TagsFilters';

const defaultStats = Object.freeze({
    match100: 0,
    matchMenor100: 0,
    noMatch: 0,
    confirmados: 0,
    aProcesar: 0,
    conError: 0,
    descartados: 0,
});

const emptyState = Object.freeze({
    icon: "error",
    title: "No hay registros para mostrar en este momento",
    description: "Intentá cambiar los filtros o actualizar más tarde.",
    iconStyles: "text-warning-20",
    titleStyles: "text-primary-10"
});

const MIN_SEARCH_LENGTH = 3;

const sanitizeFilters = (filters = {}) => {
    const sanitized = {};

    if (filters.grupo) {
        sanitized.grupo = filters.grupo;
    }

    if (filters.banco) {
        sanitized.banco = filters.banco;
    }

    if (filters.fechaDesde) {
        sanitized.fechaDesde = filters.fechaDesde;
    }

    if (filters.fechaHasta) {
        sanitized.fechaHasta = filters.fechaHasta;
    }

    if (filters.fechaEstadoDesde) {
        sanitized.fechaEstadoDesde = filters.fechaEstadoDesde;
    }

    if (filters.fechaEstadoHasta) {
        sanitized.fechaEstadoHasta = filters.fechaEstadoHasta;
    }

    if (filters.estado) {
        sanitized.estado = filters.estado;
    }

    if (filters.search && filters.search.trim().length >= MIN_SEARCH_LENGTH) {
        sanitized.search = filters.search.trim();
    }

    return sanitized;
};

/**
 * Componente principal de Matching Bancario
 * Muestra una tabla con los registros obtenidos del backend, filtros y estadísticas
 */
export default function MatchingBancario() {
    const [tabSelected, setTabSelected] = useState(() => {
        const storedTab = sessionStorage.getItem('tabSelectedMatchingBancario');
        return storedTab ? JSON.parse(storedTab) : { text: "Pendientes", id: "pendientes" };
    });

    // Custom hooks para UI y acciones
    const ui = useMatchingBancarioUI();
    const actions = useMatchingBancarioActions(ui);

    // Zustand store
    const filters = useMatchingBancarioFiltersStore((state) => state.filters);
    const setFilters = useMatchingBancarioFiltersStore((state) => state.setFilters);
    const setFilter = useMatchingBancarioFiltersStore((state) => state.setFilter);
    const clearFilter = useMatchingBancarioFiltersStore((state) => state.clearFilter);
    const pagination = useMatchingBancarioFiltersStore((state) => state.pagination);
    const sortConfig = useMatchingBancarioFiltersStore((state) => state.sortConfig);
    const initialized = useMatchingBancarioFiltersStore((state) => state.initialized);
    const initialize = useMatchingBancarioFiltersStore((state) => state.initialize);
    const setPagination = useMatchingBancarioFiltersStore((state) => state.setPagination);
    const setSortConfig = useMatchingBancarioFiltersStore((state) => state.setSortConfig);

    const debouncedSearch = useDebounce(filters.search, 300);

    // Inicializar el store
    useEffect(() => {
        if (!initialized) {
            initialize({ filters: { fechaDesde: getFechaInicialDesde() } });
        }
    }, [initialized, initialize]);

    // Sanitizar filtros
    const sanitizedFilters = useMemo(
        () =>
            sanitizeFilters({
                grupo: filters.grupo,
                banco: filters.banco,
                fechaDesde: filters.fechaDesde,
                fechaHasta: filters.fechaHasta,
                fechaEstadoDesde: filters.fechaEstadoDesde,
                fechaEstadoHasta: filters.fechaEstadoHasta,
                estado: filters.estado,
                search: debouncedSearch,
            }),
        [filters.grupo, filters.banco, filters.fechaDesde, filters.fechaHasta, filters.fechaEstadoDesde, filters.fechaEstadoHasta, filters.estado, debouncedSearch]
    );

    const filtersKey = useMemo(
        () => JSON.stringify(sanitizedFilters),
        [sanitizedFilters]
    );

    useEffect(() => {
        sessionStorage.setItem('tabSelectedMatchingBancario', JSON.stringify(tabSelected));

        setFilters({
            grupo: tabSelected.id
        });
    }, [tabSelected, setFilters]);

    useEffect(() => {
        ui.resetSelection();
    }, [filters, ui.resetSelection]);

    const page = pagination.page;
    const pageSize = pagination.pageSize;
    const offset = Math.max(0, (page - 1) * pageSize);

    // Query para obtener registros de matching bancario
    const {
        data: matchingData,
        isLoading: matchingIsLoading,
        isFetching: matchingIsFetching,
        isError: matchingIsError,
        error: matchingErrorData,
    } = useQuery({
        queryKey: ["matching-bancario", filtersKey, page, pageSize],
        enabled: initialized,
        keepPreviousData: true,
        queryFn: ({ signal }) =>
            getMatchingBancario(
                {
                    ...sanitizedFilters,
                    offset,
                    limit: pageSize,
                },
                signal
            ),
        onError: (err) =>
            errorService.handleError(err, {
                userMessage: "No pudimos obtener los registros de matching bancario.",
                origen: "MatchingBancario.matchingQuery",
            }),
    });

    // Query para obtener estadísticas
    const { data: estadisticasData } = useQuery({
        queryKey: ["matching-estadisticas", filtersKey],
        enabled: initialized,
        keepPreviousData: true,
        queryFn: ({ signal }) => getEstadisticas(sanitizedFilters, signal),
        select: (data) => parseEstadisticas(data) || defaultStats,
        onError: (err) =>
            errorService.handleError(err, {
                userMessage: "No pudimos obtener las estadísticas.",
                origen: "MatchingBancario.estadisticasQuery",
            }),
    });

    const headers = useMemo(() => {
        const baseHeaders = matchingData?.headers || {};
        return {
            ...baseHeaders,
            "x-current-page": String(page),
            "x-elements-per-page": String(pageSize),
        };
    }, [matchingData, page, pageSize]);

    const estadisticas = estadisticasData || defaultStats;

    const matchingLoading = matchingIsLoading || matchingIsFetching;

    const getMatchingError = () => {
        if (!matchingIsError) {
            return null;
        }
        if (matchingErrorData instanceof Error) {
            return matchingErrorData.message;
        }
        return "No pudimos obtener los registros.";
    };

    const matchingError = getMatchingError();

    // Handler para cambio de página
    const handlePageChange = useCallback(
        ({ page: nextPage, pageSize: nextPageSize, control }) => {
            if (control === "size" && (nextPageSize ?? pageSize) === pageSize) {
                return;
            }

            const resolvedPage = nextPage ?? page;
            const resolvedPageSize = nextPageSize ?? pageSize;

            if (resolvedPage === page && resolvedPageSize === pageSize) {
                return;
            }

            setPagination({ page: resolvedPage, pageSize: resolvedPageSize });
        },
        [page, pageSize, setPagination]
    );

    const handleSearchChange = useCallback(
        (value) => {
            if (value === "") {
                if (filters.search === "") return;
                clearFilter("search");
                return;
            }
            if (filters.search === value) return;
            setFilter("search", value);
        },
        [clearFilter, filters.search, setFilter]
    );

    // Transformar datos para incluir formato de moneda, links y componente de porcentaje
    const transformedData = useMemo(() => {
        const data = matchingData?.registros || [];
        return data.map((item) => ({
            ...item,
            acciones: <AccionesCell id={item.ID_EMAIL} />,
            EB_IMPORTE: formatCurrency(item.EB_IMPORTE),
            EXT_IMPORTE: formatCurrency(item.EXT_IMPORTE),
            PORC_MATCH: <PorcentajeMatchCell value={item.PORC_MATCH} />,
            flag: item.EB_ERROR ? {
                description: item.EB_ERROR,
                icon: "info",
                color: "error",
            } : null
        }));
    }, [matchingData?.registros]);

    const hasErrors = useMemo(() => {
        const data = matchingData?.registros || [];
        return data.some((item) => item.EB_ERROR);
    }, [matchingData?.registros]);

    // Handlers de acciones masivas
    const handleConfirm = useCallback(() => {
        if (ui.selectedRows.length > 0) {
            ui.openModal('confirm');
        }
    }, [ui]);

    const handleDescartar = useCallback(() => {
        if (ui.selectedRows.length > 0) {
            ui.openModal('descartar');
        }
    }, [ui]);

    const handleReprocesar = useCallback(() => {
        if (ui.selectedRows.length > 0) {
            ui.openModal('reprocesar');
        }
    }, [ui]);

    if (matchingError) {
        return (
            <div className="flex flex-col justify-center items-center h-screen gap-4">
                <Icon size="extra-large" variant="error" color="error" />
                <h2 className="typography-h2 text-error-50">Error al cargar los datos</h2>
                <p className="typography-b1 text-tertiary-60">{matchingError}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-primary-50 text-white rounded hover:bg-primary-60"
                >
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-5 relative">
            {/* Alerta posicionada absolutamente a la derecha */}
            {ui.alert.show && (
                <div className="absolute top-3 right-4 z-50">
                    <Alert
                        variant={ui.alert.variant}
                        title={ui.alert.title}
                        description={ui.alert.description}
                        onClose={ui.hideAlert}
                        autoDismiss={true}
                        containerStyle={ui.alert.containerStyle || ''}
                    />
                </div>
            )}

            <div className='mb-5'>
                <Text variant="h6" className="text-primary-20">
                    Gestión de Matching Bancario
                </Text>

                <Text variant="body2">
                    Acá podés consultar y gestionar los registros de matching bancario.
                </Text>
            </div>

            {/* Cards de Estadísticas */}
            <IndicadoresGrid config={indicadoresBancarioConfig} estadisticas={estadisticas} className="mb-5" />

            {/* Filtros y Tags */}
            <div className='flex items-center gap-4 mb-5'>
                <SearchBar
                    placeholder="Buscar por carátula, número o importe de embargo"
                    searchValue={filters.search}
                    setSearchValue={handleSearchChange}
                    classNameContainer="min-w-[450px]"
                    opciones={[]}
                />
                <ModalFilter />
                <TagsFilters />

                {tabSelected.id !== "historial" && (
                    <div className='ml-auto'>
                        <DropdownButton
                            textButton="Acciones masivas"
                            menuOptions={[
                                { opcion: "Confirmar", onClick: handleConfirm },
                                { opcion: "Reprocesar", onClick: handleReprocesar },
                                { opcion: "Descartar", onClick: handleDescartar },
                            ]}
                            className={{
                                dropdown: '!h-auto',
                                opcionButton: 'text-tertiary-30',
                                listOpcionButton: 'z-50',
                            }}
                            disabled={ui.selectedRows.length === 0}
                        />
                    </div>
                )}
            </div>
            <div className="w-full flex justify-between border-b border-primary-10 mb-5">
                <div className="flex">
                    <Tabs
                        className="w-[284px]"
                        classNameTabButton="h-[32px]"
                        underline={false}
                        items={[
                            { text: "Pendientes", id: "pendientes" },
                            { text: "Historial", id: "historial" }
                        ]}
                        selected={tabSelected}
                        setSelected={setTabSelected}
                    />
                </div>
            </div>

            {/* Tabla con Paginación */}
            <MatchingBancarioTable
                data={transformedData}
                isLoading={matchingLoading}
                onPageChange={handlePageChange}
                headers={headers}
                page={page}
                pageSize={pageSize}
                setSelected={ui.setSelectedRows}
                selected={ui.selectedRows}
                emptyState={emptyState}
                hasErrors={hasErrors}
                selectable={tabSelected.id !== "historial"}
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
            />

            {/* Spinner de Procesamiento */}
            {ui.isProcessing && (
                <div className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 z-50">
                    <Icon variant="loading" classNameIcon="size-16 text-[64px]" />
                </div>
            )}

            {/* Modal de Confirmación */}
            {ui.modals.confirm && (
                <ModalConfirm
                    open={ui.modals.confirm}
                    setOpen={(open) => open ? ui.openModal('confirm') : ui.closeModal('confirm')}
                    selectedRows={ui.selectedRows}
                    onConfirm={actions.handleConfirmAccept}
                />
            )}

            {/* Modal de Resultados */}
            {ui.modals.result && (
                <ModalResultado
                    open={ui.modals.result}
                    setOpen={(open) => open ? ui.openModal('result') : ui.closeModal('result')}
                    resultData={ui.resultData}
                    onClose={actions.handleCloseResultModal}
                />
            )}

            {/* Modal de Confirmación de Descarte */}
            {ui.modals.descartar && (
                <ModalAccionMasiva
                    open={ui.modals.descartar}
                    setOpen={(open) => open ? ui.openModal('descartar') : ui.closeModal('descartar')}
                    selectedRows={ui.selectedRows}
                    icon="error"
                    iconColor="text-warning-20"
                    iconSize="extra-large"
                    title="Descartar embargo"
                    message="Si descartas el embargo, no podrá deshacer esta acción."
                    cancelText="Volver al embargo"
                    confirmText="Descartar"
                    onConfirm={actions.handleDescartarAccept}
                    width="408px"
                    height="auto"
                />
            )}

            {/* Modal de Confirmación de Reprocesar */}
            {ui.modals.reprocesar && (
                <ModalAccionMasiva
                    open={ui.modals.reprocesar}
                    setOpen={(open) => open ? ui.openModal('reprocesar') : ui.closeModal('reprocesar')}
                    selectedRows={ui.selectedRows}
                    icon="error"
                    iconColor="text-warning-20"
                    iconSize="extra-large"
                    title="Reprocesar embargo"
                    message="Si reprocesás el embargo, no podrá deshacer esta acción."
                    cancelText="Volver al embargo"
                    confirmText="Reprocesar"
                    onConfirm={actions.handleReprocesarAccept}
                    width="408px"
                    height="auto"
                />
            )}
        </div>
    );
}
